import React, { useState, useEffect, useRef } from 'react';
import { post } from 'helpers/api';
import { format } from 'date-fns';
import { NonBasicCardStyling } from './util';
import { type PreviewEyeProps } from './preview_eye';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';

type ActivitySummaryData = {
  content: string;
  updatedAt: string;
};

export type ActivitySummaryProps = {
  isHidden?: boolean;
  preview?: boolean;
  previewEye: PreviewEyeProps;
  projectId?: string;
  activitySummary: ActivitySummaryData;
}

export default function ActivitySummaryCard(props: ActivitySummaryProps) {
  const { preview, activitySummary } = props;
  const [content, setContent] = useState(activitySummary.content || '');
  const [contentUpdatedAt, setContentUpdatedAt] = useState(activitySummary.updatedAt || '');
  const [isSaving, setIsSaving] = useState(false);
  const lastSavedContent = useRef(activitySummary.content || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;

    const checkValueChange = () => {
      if (textarea.value !== content) {
        saveContent(textarea.value);
      }
    };

    // Listen for custom even that will be triggered in activity_summary.js
    // when the text is used from AI generated content in AL chat
    textarea.addEventListener('ai-content-update.activity-summary', checkValueChange);

    return () => {
      textarea.removeEventListener('ai-content-update.activity-summary', checkValueChange);
    };
  }, [content]);

  const handleContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(event.target.value);
  };

  const saveContentOnBlur = async () => {
    await saveContent(content);
  };

  const saveContent = async (contentToSave:string) => {
    if (contentToSave !== lastSavedContent.current) {
      setIsSaving(true);

      try {
        const response = await post(
          `/projects/${props.projectId}/client_report/update_activity_summary`,
          { summary_content: contentToSave }
        );
        setContent(response.content || '');
        setContentUpdatedAt(response.updatedAt || '');
        lastSavedContent.current = response.content || '';
      } catch (error) {
        setContentUpdatedAt('ERROR');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const getRelativeTime = (dateString: string) => {
    if (!dateString || dateString === 'ERROR') return null;
    const now = new Date();
    const updated = new Date(dateString);
    const diffMs = now.getTime() - updated.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `Last saved ${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Last saved ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `Last saved ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  return (
    <div className={NonBasicCardStyling}>
      <div className="d-flex flex-column justify-content-between align-items-start">
        <div className="d-flex flex-row justify-content-between w-100">
          <h4 className="m-0 fw-normal">Activity Summary</h4>
          { !preview && props.previewEye }
        </div>
        { !preview && contentUpdatedAt && contentUpdatedAt !== 'ERROR' && (
          <div className="activity-status-text text-muted" style={{ fontSize: '0.95em' }}>
            <FontAwesomeIcon className="text-success" icon={faCircleSmall} style={{ marginRight: '5px' }} />
            { getRelativeTime(contentUpdatedAt) }
          </div>
        )}
      </div>
      <div
        className="ai-enabled h-100 activity-summary-container"
        data-add-control="true"
        data-controller="ai-interaction"
        data-key="activity_summary"
      >
        <textarea
          className="form-control w-100 mb-4"
          placeholder={preview ? '' : 'Add an activity summary or generate a new one'}
          readOnly={preview}
          ref={textareaRef}
          style={{
            height: '80%',
            backgroundColor: props.isHidden
              ? 'var(--ds-storm-grey-100, #eeeef2)'
              : preview
                ? '#fff'
                : undefined
          }}
          value={content}
          onBlur={saveContentOnBlur}
          onChange={handleContentChange}
          onMouseDown={preview ? e => e.preventDefault() : undefined}
        />
      </div>
    </div>
  );
}
